import { useEffect, useRef, useCallback } from 'react';

import socketService from '../../domain/services/api/socket';

import { agendaPollingMs } from '../../domain/constant/crm/crm.agenda.constant';

const REFRESH_DEBOUNCE_MS = 350;
const RECONNECT_REFRESH_THROTTLE_MS = 2000;

// a api manda a lista vazia quando o aviso não cabe no payload: pode ser qualquer oportunidade
const mayConcernOpportunity = (event = {}, opportunityId) =>
    !opportunityId
    || !Array.isArray(event.opportunityIds)
    || !event.opportunityIds.length
    || event.opportunityIds.some(id => Number(id) === Number(opportunityId));

const useAgendaRealtimeSync = ({ sellerId, onRefresh, knownVersion = null, opportunityId = null }) => {
    const refreshInFlightRef = useRef(false);
    const pendingRefreshRef = useRef(false);
    const pendingRefreshSourceRef = useRef('polling');
    const refreshDebounceRef = useRef(null);
    const realtimeVersionRef = useRef(null);
    const lastRefreshStartedAtRef = useRef(0);
    const onRefreshRef = useRef(onRefresh);

    useEffect(() => {
        onRefreshRef.current = onRefresh;
    }, [onRefresh]);

    const executeRefresh = useCallback(async (source = 'polling') => {
        if (refreshInFlightRef.current) {
            pendingRefreshRef.current = true;
            pendingRefreshSourceRef.current = source;
            return;
        }

        refreshInFlightRef.current = true;
        lastRefreshStartedAtRef.current = Date.now();

        try {
            await onRefreshRef.current(source);
        } finally {
            refreshInFlightRef.current = false;

            if (pendingRefreshRef.current) {
                pendingRefreshRef.current = false;
                const pendingSource = pendingRefreshSourceRef.current;
                pendingRefreshSourceRef.current = 'polling';
                executeRefresh(pendingSource);
            }
        }
    }, []);

    const queueRefresh = useCallback((source = 'polling') => {
        if (refreshDebounceRef.current)
            clearTimeout(refreshDebounceRef.current);

        refreshDebounceRef.current = setTimeout(() => {
            executeRefresh(source);
        }, REFRESH_DEBOUNCE_MS);
    }, [executeRefresh]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (document.visibilityState === 'hidden')
                return;

            queueRefresh('polling');
        }, agendaPollingMs);

        return () => clearInterval(interval);
    }, [queueRefresh]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible')
                queueRefresh('visibility');
        }

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [queueRefresh]);

    useEffect(() => {
        if (!sellerId)
            return;

        realtimeVersionRef.current = null;

        const onAgendaReorder = (event = {}) => {

            if (event.sellerId && Number(event.sellerId) !== Number(sellerId))
                return;

            if (!mayConcernOpportunity(event, opportunityId))
                return;

            const version = Number(event.version);
            if (version <= Number(realtimeVersionRef.current || 0))
                return;

            realtimeVersionRef.current = version;
            queueRefresh('realtime');
        }

        const onConnect = () => {
            const elapsed = Date.now() - lastRefreshStartedAtRef.current;
            if (elapsed < RECONNECT_REFRESH_THROTTLE_MS)
                return;

            queueRefresh('reconnect');
        }

        const unsubscribeReorder = socketService.subscribe('agenda:reorder', onAgendaReorder);
        const unsubscribeConnect = socketService.subscribe('connect', onConnect);

        return () => {
            unsubscribeReorder();
            unsubscribeConnect();
        };
    }, [queueRefresh, sellerId, opportunityId]);

    // a versão que veio na carga da agenda semeia o guard; roda depois da inscrição, que
    // zera a versão, para que a troca de vendedor não herde a contagem do anterior
    useEffect(() => {
        if (!Number.isFinite(knownVersion))
            return;

        const currentVersion = Number(realtimeVersionRef.current || 0);
        if (knownVersion > currentVersion)
            realtimeVersionRef.current = knownVersion;
    }, [knownVersion]);

    useEffect(() => () => {
        if (refreshDebounceRef.current)
            clearTimeout(refreshDebounceRef.current);
    }, []);

    return {
        queueRefresh,
        executeRefresh,
    };
};

export default useAgendaRealtimeSync;
